import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Button, Stack, Text, Progress, Loader, ThemeIcon, Group,
} from '@mantine/core';
import {
    IconFileSpreadsheet, IconCheck, IconAlertTriangle, IconBraces,
} from '@tabler/icons-react';
import { toast } from 'react-toastify';

const FORMATS = {
    xlsx: {
        ext: '.xlsx',
        mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        pickerDescription: 'Excel',
    },
    json: {
        ext: '.zip',
        mime: 'application/zip',
        pickerDescription: 'ZIP',
    },
};

function formatBytes(bytes) {
    if (!bytes || bytes < 0) return '0 MB';
    const mb = bytes / (1024 * 1024);
    if (mb < 1) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${mb.toFixed(1)} MB`;
}

function defaultFileName(prefix, ext) {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${prefix}_${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}${ext}`;
}

function filenameFromDisposition(cd) {
    if (!cd) return '';
    const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(cd);
    if (star) {
        try {
            return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
        } catch (e) {
            return '';
        }
    }
    const plain = /filename="?([^";]+)"?/i.exec(cd);
    return plain ? plain[1].trim() : '';
}

function saveBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 2000);
}

function ExportPanel(props) {
    const {
        excelFn, jsonFn, fileNamePrefix, locale, title, desc, L,
    } = props;

    const [status, setStatus] = useState('idle');
    const [bytes, setBytes] = useState(0);
    const [totalBytes, setTotalBytes] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');

    const handleDownload = useCallback(async (format) => {
        const fmt = FORMATS[format] || FORMATS.xlsx;
        const exportFn = format === 'json' ? jsonFn : excelFn;

        setStatus('downloading');
        setBytes(0);
        setTotalBytes(null);
        setErrorMsg('');

        let handle = null;
        if (window.showSaveFilePicker) {
            try {
                handle = await window.showSaveFilePicker({
                    suggestedName: defaultFileName(fileNamePrefix, fmt.ext),
                    types: [{
                        description: fmt.pickerDescription,
                        accept: { [fmt.mime]: [fmt.ext] },
                    }],
                });
            } catch (err) {
                if (err && err.name === 'AbortError') { setStatus('idle'); return; }
                handle = null;
            }
        }

        try {
            const res = await exportFn(locale);
            if (!res.ok) {
                const msg = res.status === 404 ? L.empty : L.error;
                setStatus('error');
                setErrorMsg(msg);
                toast.error(msg, { theme: 'colored' });
                return;
            }

            const total = Number(res.headers.get('content-length')) || null;
            const name = filenameFromDisposition(res.headers.get('content-disposition'))
                || defaultFileName(fileNamePrefix, fmt.ext);
            setTotalBytes(total);

            const reader = res.body.getReader();
            const writable = handle ? await handle.createWritable() : null;
            const chunks = handle ? null : [];
            let loaded = 0;
            let done = false;
            while (!done) {
                // eslint-disable-next-line no-await-in-loop
                const chunk = await reader.read();
                done = chunk.done;
                if (chunk.value) {
                    loaded += chunk.value.length;
                    setBytes(loaded);
                    if (writable) {
                        // eslint-disable-next-line no-await-in-loop
                        await writable.write(chunk.value);
                    } else {
                        chunks.push(chunk.value);
                    }
                }
            }

            if (loaded === 0) {
                if (writable) await writable.abort();
                setStatus('error');
                setErrorMsg(L.empty);
                toast.warning(L.empty, { theme: 'colored' });
                return;
            }

            if (writable) {
                await writable.close();
            } else {
                const blob = new Blob(chunks, { type: fmt.mime });
                saveBlob(blob, name);
            }

            setStatus('done');
            toast.success(L.done, { theme: 'colored' });
        } catch (err) {
            setStatus('error');
            const msg = err instanceof TypeError ? (L.blocked || L.error) : L.error;
            setErrorMsg(msg);
            toast.error(msg, { theme: 'colored' });
        }
    }, [excelFn, jsonFn, fileNamePrefix, locale, L]);

    const downloadPercent = totalBytes ? Math.min(100, Math.round((bytes / totalBytes) * 100)) : 0;

    const downloadButtons = (
        <Group position="left" spacing="sm">
            <Button
                color="blue"
                leftIcon={<IconFileSpreadsheet size={18} />}
                onClick={() => handleDownload('xlsx')}
            >
                {L.downloadExcel}
            </Button>
            <Button
                color="teal"
                variant="light"
                leftIcon={<IconBraces size={18} />}
                onClick={() => handleDownload('json')}
            >
                {L.downloadJson}
            </Button>
        </Group>
    );

    return (
        <div className="tw-bg-paper tw-border tw-border-solid tw-border-line tw-rounded-brand-lg tw-shadow-card tw-p-5 md:tw-p-6">
            <h2 className="tw-m-0 tw-mb-2 tw-text-ink tw-text-xl tw-font-bold tw-font-inter">{title}</h2>
            <p className="tw-text-muted tw-text-sm tw-mb-5 tw-font-inter tw-leading-relaxed">{desc}</p>

            <Stack spacing="md">
                {status === 'idle' && (
                    <React.Fragment>
                        <Text size="sm" color="dimmed">{L.warning}</Text>
                        {downloadButtons}
                    </React.Fragment>
                )}

                {status === 'downloading' && (
                    <Stack spacing="xs">
                        <Group spacing="xs">
                            <Loader size="sm" />
                            <Text size="sm" weight={600}>{L.downloading}</Text>
                        </Group>
                        {totalBytes ? <Progress value={downloadPercent} striped animate /> : null}
                        <Text size="xs" color="dimmed">
                            {formatBytes(bytes)}
                            {totalBytes ? ` / ${formatBytes(totalBytes)}` : ''}
                            {' · '}
                            {L.downloadingHint}
                        </Text>
                    </Stack>
                )}

                {status === 'done' && (
                    <React.Fragment>
                        <Group spacing="xs">
                            <ThemeIcon color="teal" radius="xl">
                                <IconCheck size={18} />
                            </ThemeIcon>
                            <Text size="sm" weight={600}>{L.done}</Text>
                        </Group>
                        {downloadButtons}
                    </React.Fragment>
                )}

                {status === 'error' && (
                    <React.Fragment>
                        <Group spacing="xs" align="flex-start" noWrap>
                            <ThemeIcon color="red" radius="xl">
                                <IconAlertTriangle size={18} />
                            </ThemeIcon>
                            <Text size="sm" weight={600}>{errorMsg || L.error}</Text>
                        </Group>
                        {downloadButtons}
                    </React.Fragment>
                )}
            </Stack>
        </div>
    );
}

ExportPanel.propTypes = {
    excelFn: PropTypes.func.isRequired,
    jsonFn: PropTypes.func.isRequired,
    fileNamePrefix: PropTypes.string.isRequired,
    locale: PropTypes.string,
    title: PropTypes.string,
    desc: PropTypes.string,
    L: PropTypes.object.isRequired,
};

export default ExportPanel;
